/*!
 * RebelsMarket Fit Recommender — pure recommendation engine.
 *
 * No DOM, no dependencies. Give it the wizard's answers, get back a size + width.
 * Logic mirrors docs/rebelsmarket-fit-widget-spec.md. All numbers are SEED values —
 * calibrate brand offsets, sneaker fit-style, and width girths against real returns +
 * your own last measurements.
 *
 * Usage:
 *   const out = FitRecommender.recommend(answers);
 *   // answers = {
 *   //   gender: 'men' | 'women',
 *   //   anchors: [{ brand:'nike', size:10.5, fit:'snug' }, ...],   // sizes you already know
 *   //   measurement: { leftLen, rightLen, leftGirth, rightGirth }, // mm, optional
 *   //   width: { source:'question'|'known', answer:'tight', letter:'D' } // optional
 *   // }
 */
(function (root) {
  'use strict';

  // ---- Config (tunable seeds) ------------------------------------------------
  var CONFIG = {
    size_range: { men: { min: 5.5, max: 16 }, women: { min: 7, max: 16 } }, // offered US ranges: every pair is on a men's last; women's listings carry 7-16 (a men's 5.5 last would be a women's 6.5, not listed)
    round_bias: 'down',            // near-tie -> smaller size (leather stretches)
    tie_tolerance_mm: 2.0,
    agreement_tolerance_size: 0.5, // anchors within this many US sizes "agree"
    women_size_offset: 1,          // OUR women's US = men's US + 1 for the same foot (Brannock: 3L-21 vs 3L-22). We sell true to Brannock size. Keep equal to rm-forge WOMEN_SIZE_OFFSET.
    brand_women_offset: 1.5,       // retail brands (Nike, Adidas, Cole Haan...) label women's = men's + 1.5; used only to read a customer's size in another brand
    outlier_drop_mm: 12,           // ~1.5 US sizes; estimates beyond this from median are dropped
    reconcile_conflict_mm: 10,     // brand-only: if kept estimates still span more than this
                                   // (~1.2 US sizes) we refuse to guess and ask them to measure

    sneaker_fit_style_mm: { snug: 0, just_right: -4, roomy: -8 },
    sneaker_offset_default: -0.5,  // for sneaker brands with NO length chart

    // Current-shoe problem nudges (seeds — tune from returns). '+' = longer / wider.
    problem_length_mm: { runs_long: -6, cramped_length: 6, tight_toe: 5, heel_slip: -3 },
    problem_width_step: { pinch_sides: 1, sloppy: -1, bunions: 1 },

    width: {
      d_girth_ratio: 0.925,        // Standard(D) ball girth ≈ 0.925 × foot length
      grid_offsets_mm: {           // ball-girth offset vs D, full A–5E ladder
        A: -19.05, B: -12.70, C: -6.35, D: 0.0, E: 6.35,
        '2E': 12.70, '3E': 19.05, '4E': 25.40, '5E': 31.75
      },
      // Every pair is made to order — no stock/MTO split. This list is only the set the
      // coarse "how does width feel" question can land on.
      question_widths: ['B', 'D', '2E', '3E', '4E'],
      // The widths we actually manufacture. The recommender must only ever emit one of
      // these — the in-between grades (A/C/E) exist solely to interpret measured girth
      // (see MEASUREMENT-V3-SPEC.md §3.3). Any raw width is snapped to the nearest here.
      supported_widths: ['B', 'D', '2E', '3E', '4E']
    },
    standard_width: { men: 'D', women: 'B' },
    // known brand width letter -> RB ladder
    width_letter_map: { A: 'A', B: 'B', C: 'C', D: 'D', E: 'E', EE: '2E', EEE: '3E', EEEE: '4E', EEEEE: '5E' }
  };

  var LADDER = ['A', 'B', 'C', 'D', 'E', '2E', '3E', '4E', '5E'];
  var WIDTH_NAME = {
    A: 'Extra Narrow', B: 'Narrow', C: 'Narrow+', D: 'Standard', E: 'Standard+',
    '2E': 'Wide', '3E': 'Extra wide', '4E': 'Widest', '5E': 'XX-Wide'
  };

  // ---- Charts (men's US → foot length mm; women derived by +1 size, Brannock) --
  // The published RebelsMarket men's size chart (US → foot length). The engine MUST match
  // the printed size guide, so whole and .5 sizes use the chart's exact cm values (×10).
  // The chart only printed whole sizes 12–16; the 12.5/13.5/14.5/15.5 entries are the
  // midpoints between neighbours (replace with exact values if the guide adds them).
  // Synced to the published Fit Guide (PDF page 5). Foot length mm per men's US size.
  var RB_MEN_MM = {
    5.5: 233, 6: 237, 6.5: 241, 7: 246, 7.5: 250, 8: 254, 8.5: 258,
    9: 262, 9.5: 267, 10: 271, 10.5: 275, 11: 279, 11.5: 284, 12: 288,
    12.5: 292, 13: 296, 13.5: 301, 14: 305, 14.5: 309, 15: 313, 15.5: 318, 16: 322
  };
  var EU_UK = {
    5: { eu: 38, uk: 4 }, 5.5: { eu: 38.5, uk: 4.5 }, 6: { eu: 39, uk: 5 }, 6.5: { eu: 39.5, uk: 5.5 },
    7: { eu: 40, uk: 6 }, 7.5: { eu: 40.5, uk: 6.5 }, 8: { eu: 41, uk: 7 }, 8.5: { eu: 41.5, uk: 7.5 },
    9: { eu: 42, uk: 8 }, 9.5: { eu: 42.5, uk: 8.5 }, 10: { eu: 43, uk: 9 }, 10.5: { eu: 43.5, uk: 9.5 },
    11: { eu: 44, uk: 10 }, 11.5: { eu: 44.5, uk: 10.5 }, 12: { eu: 45, uk: 11 }, 12.5: { eu: 45.5, uk: 11.5 },
    13: { eu: 46, uk: 12 }, 13.5: { eu: 46.5, uk: 12.5 }, 14: { eu: 47, uk: 13 }, 14.5: { eu: 47.5, uk: 13.5 },
    15: { eu: 48, uk: 14 }, 15.5: { eu: 48.5, uk: 14.5 }, 16: { eu: 49, uk: 15 }
  };

  // Brand length charts (men's US → foot length mm)
  var COLE_HAAN_MM = {
    6: 235, 6.5: 240, 7: 245, 7.5: 250, 8: 255, 8.5: 260, 9: 265, 9.5: 270, 10: 275,
    10.5: 280, 11: 285, 11.5: 290, 12: 295, 12.5: 300, 13: 305, 14: 310, 15: 315, 16: 320
  };
  var NIKE_MEN_MM = {
    6: 237, 6.5: 241, 7: 246, 7.5: 249, 8: 254, 8.5: 259, 9: 262, 9.5: 267, 10: 271,
    10.5: 275, 11: 279, 11.5: 284, 12: 287, 12.5: 292, 13: 297, 13.5: 300, 14: 305
  };
  // Adidas men's US → foot length mm, from adidas.com heel-to-toe size chart.
  var ADIDAS_MEN_MM = {
    5: 229, 5.5: 233, 6: 238, 6.5: 242, 7: 246, 7.5: 250, 8: 255, 8.5: 259, 9: 263,
    9.5: 267, 10: 271, 10.5: 276, 11: 280, 11.5: 284, 12: 288, 12.5: 293, 13: 297,
    13.5: 301, 14: 305, 14.5: 310, 15: 314, 16: 318
  };
  // New Balance men's US → foot length mm, from newbalance.com size chart.
  // NB (and J&M below) publish at ~1 cm/size, coarser than the 8.47 mm/size barleycorn
  // our last uses, so they read progressively larger than Brannock (≈ +0.7 size at 8,
  // ≈ +1 at 10). Treat as the brand's own spec; calibrate against returns (ENG-83).
  var NEW_BALANCE_MEN_MM = {
    5: 230, 5.5: 235, 6: 240, 6.5: 245, 7: 250, 7.5: 255, 8: 260, 8.5: 265, 9: 270,
    9.5: 275, 10: 280, 10.5: 285, 11: 290, 11.5: 295, 12: 300, 12.5: 305, 13: 310,
    13.5: 315, 14: 320, 15: 330, 16: 340
  };
  // Johnston & Murphy men's US → foot length mm (their US/EU/UK/JP-cm chart; same ~1 cm/size caveat).
  var JOHNSTON_MURPHY_MEN_MM = {
    6: 240, 6.5: 245, 7: 250, 7.5: 255, 8: 260, 8.5: 265, 9: 270, 9.5: 275, 10: 280,
    10.5: 285, 11: 290, 11.5: 295, 12: 300, 13: 310, 14: 320, 15: 330, 16: 340
  };

  // Brand registry — exposed so the UI can build its dropdowns from one source.
  var BRANDS = {
    rebelsmarket:    { label: 'RebelsMarket / true size', kind: 'known' },
    // Brannock = the store measuring device, i.e. the scale our own last is anchored to.
    // A Brannock reading maps straight through RB_MEN_MM with no brand offset/guess.
    brannock:        { label: 'Brannock (store-measured)', kind: 'known', chart: RB_MEN_MM },
    cole_haan:       { label: 'Cole Haan',        kind: 'dress',   chart: COLE_HAAN_MM },
    johnston_murphy: { label: 'Johnston & Murphy', kind: 'dress',  chart: JOHNSTON_MURPHY_MEN_MM },
    other_dress:     { label: 'Other dress shoe', kind: 'dress',   offset: 0.0 },
    nike:            { label: 'Nike',             kind: 'sneaker', chart: NIKE_MEN_MM },
    adidas:          { label: 'Adidas',           kind: 'sneaker', chart: ADIDAS_MEN_MM },
    new_balance:     { label: 'New Balance',      kind: 'sneaker', chart: NEW_BALANCE_MEN_MM },
    other_sneaker:   { label: 'Other sneaker',    kind: 'sneaker', offset: -0.5 },
    // Generic buckets for the quiz (no specific brand chosen). Dress shoes ≈ true to our
    // last; sneakers ≈ 1:1 to our last with the fit-feel adjustment (see §3d / §4a).
    generic_dress:   { label: 'Dress shoes',      kind: 'dress',   chart: RB_MEN_MM },
    generic_sneaker: { label: 'Sneakers',         kind: 'sneaker', chart: RB_MEN_MM }
  };

  // ---- Helpers ---------------------------------------------------------------
  function sortedKeys(chart) { return Object.keys(chart).map(Number).sort(function (a, b) { return a - b; }); }

  function interp(chart, size) {           // linear interpolation with end-clamping
    var ks = sortedKeys(chart);
    if (size <= ks[0]) return chart[ks[0]];
    if (size >= ks[ks.length - 1]) return chart[ks[ks.length - 1]];
    for (var i = 0; i < ks.length - 1; i++) {
      if (size >= ks[i] && size <= ks[i + 1]) {
        var t = (size - ks[i]) / (ks[i + 1] - ks[i]);
        return chart[ks[i]] + t * (chart[ks[i + 1]] - chart[ks[i]]);
      }
    }
    return chart[ks[0]];
  }

  function median(arr) {
    var s = arr.slice().sort(function (a, b) { return a - b; });
    var m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }

  function rbChart(gender) {               // women's chart derived from men's + women_size_offset (Brannock)
    if (gender !== 'women') return RB_MEN_MM;
    var out = {};
    for (var k in RB_MEN_MM) out[(+k) + CONFIG.women_size_offset] = RB_MEN_MM[k];
    return out;
  }

  function euUk(size, gender) {
    var menSize = gender === 'women' ? Math.round((size - CONFIG.women_size_offset) * 2) / 2 : size;
    var m = EU_UK[menSize];
    if (!m) return { eu: null, uk: null, approx: true };
    // women's EU ≈ same last; women's UK = women's US − 2 (the store's published
    // women's chart, and the inverse of regionToUs). Both approximate.
    return gender === 'women'
      ? { eu: m.eu, uk: size - 2, approx: true }
      : { eu: m.eu, uk: m.uk, approx: false };
  }

  // Convert a size entered in UK/EU into the gender-appropriate US number.
  function regionToUs(size, gender, region) {
    size = parseFloat(size);
    if (isNaN(size)) return size;
    if (region === 'UK') return gender === 'women' ? size + 2 : size + 1;
    if (region === 'EU') {
      var best = null, bd = Infinity;
      for (var us in EU_UK) { var d = Math.abs(EU_UK[us].eu - size); if (d < bd) { bd = d; best = +us; } }
      return gender === 'women' ? best + CONFIG.women_size_offset : best;
    }
    return size; // US
  }

  // Convert one "I know my size" anchor into an estimated foot length (mm).
  function anchorToLength(a, gender, cfg) {
    var b = BRANDS[a.brand];
    if (!b || a.size == null || isNaN(a.size)) return null;
    // A women's size in OUR scale or Brannock is men's + 1; a retail brand's women's label is men's + 1.5.
    var womenOff = b.kind === 'known' ? cfg.women_size_offset : cfg.brand_women_offset;
    var mensEquiv = gender === 'women' ? a.size - womenOff : a.size;

    if (b.chart) {                          // length-charted brand — trust the length
      var mm = interp(b.chart, mensEquiv);
      var adj = (a.fit != null && cfg.sneaker_fit_style_mm[a.fit] != null) ? cfg.sneaker_fit_style_mm[a.fit] : 0;
      mm += adj;
      return { mm: mm, source: 'chart',
        note: b.label + ' ' + a.size + (adj ? ' (fits "' + a.fit + '", ' + (adj > 0 ? '+' : '') + adj + ' mm)' : '') +
              ' → ' + Math.round(mm) + ' mm.' };
    }

    if (b.kind === 'known') {               // RB / true size given directly
      return { mm: interp(RB_MEN_MM, mensEquiv), source: 'known',
        note: 'Using your known true size directly.' };
    }

    // offset brand (no length chart): shift the size, then read our own chart
    var off = (b.offset != null) ? b.offset : (b.kind === 'sneaker' ? cfg.sneaker_offset_default : 0);
    var mm2 = interp(RB_MEN_MM, mensEquiv + off);
    return { mm: mm2, source: b.kind === 'sneaker' ? 'sneaker_offset' : 'dress_offset',
      note: b.label + ' ' + a.size + ' runs ' + (off < 0 ? 'large' : 'true') + '; offset ' + off +
            ' size → ' + Math.round(mm2) + ' mm.' };
  }

  function nearestRbSize(flMm, gender, cfg) {
    var chart = rbChart(gender), sizes = sortedKeys(chart);
    if (flMm <= chart[sizes[0]]) return sizes[0];
    if (flMm >= chart[sizes[sizes.length - 1]]) return sizes[sizes.length - 1];
    for (var i = 0; i < sizes.length - 1; i++) {
      var lo = sizes[i], hi = sizes[i + 1];
      if (flMm >= chart[lo] && flMm <= chart[hi]) {
        var dlo = flMm - chart[lo], dhi = chart[hi] - flMm;
        if (Math.abs(dlo - dhi) <= cfg.tie_tolerance_mm) return cfg.round_bias === 'down' ? lo : hi;
        return dlo < dhi ? lo : hi;
      }
    }
    return sizes[0];
  }

  function widthFromGirth(girth, flMm, cfg) {
    // Pick the SUPPORTED width whose expected ball girth is closest to the measurement
    // (spec §4.3). We only compare widths we actually make, so the result never needs snapping.
    var d = cfg.width.d_girth_ratio * flMm, best = null, bd = Infinity;
    var sup = cfg.width.supported_widths || CONFIG.width.supported_widths;
    for (var n = 0; n < sup.length; n++) {
      var code = sup[n], off = cfg.width.grid_offsets_mm[code];
      if (off == null) continue;
      var diff = Math.abs((d + off) - girth);
      if (diff < bd) { bd = diff; best = code; }
    }
    return best;
  }

  function shiftWidth(code, step) {
    var idx = LADDER.indexOf(code); if (idx < 0) idx = LADDER.indexOf('D');
    return LADDER[Math.max(0, Math.min(LADDER.length - 1, idx + step))];
  }

  // Snap any width letter to the nearest MANUFACTURED width. Ties round to the wider
  // grade (a shoe slightly roomy in volume is safer than one that's too tight).
  function snapToSupported(code, cfg) {
    var sup = cfg.width.supported_widths || CONFIG.width.supported_widths;
    if (sup.indexOf(code) >= 0) return code;
    var idx = LADDER.indexOf(code);
    if (idx < 0) return cfg.standard_width.men;           // unknown letter -> D (Standard)
    var best = sup[0], bd = Infinity;
    for (var n = 0; n < sup.length; n++) {
      var si = LADDER.indexOf(sup[n]), d = Math.abs(si - idx);
      if (d < bd || (d === bd && si > LADDER.indexOf(best))) { bd = d; best = sup[n]; }
    }
    return best;
  }

  function widthFromQuestion(answer, gender, cfg) {
    // A coarse fit question only ever lands on a STOCK width (B/D/2E/3E/4E).
    // The in-between grades (A/C/E/3E/5E) are reserved for measured girth.
    var stock = cfg.width.question_widths;              // coarse options: ['B','D','2E','3E','4E']
    var base = stock.indexOf(cfg.standard_width[gender]); // men -> D, women -> B
    if (base < 0) base = 0;
    var step = { roomy: -1, just_right: 0, tight: 1, painful: 2 }[answer];
    if (step == null) step = 0;
    var i = Math.max(0, Math.min(stock.length - 1, base + step));
    return stock[i];
  }

  function resolveWidth(answers, girth, flMm, gender, cfg, rationale) {
    var raw, source, w = answers.width || {};
    if (girth) {
      raw = widthFromGirth(girth, flMm, cfg); source = 'measured';
      rationale.push('Measured ball girth ' + girth + ' mm → width ' + raw + '.');
    } else if (w.source === 'known' && w.letter) {
      raw = cfg.width_letter_map[w.letter] || w.letter; source = 'known';
      rationale.push('You told us your width is ' + w.letter + ' → ' + raw + '.');
    } else if (w.source === 'question' && w.answer) {
      raw = widthFromQuestion(w.answer, gender, cfg); source = 'question';
      rationale.push('Fit feel "' + w.answer + '" → width ' + raw + '.');
    } else {
      raw = cfg.standard_width[gender]; source = 'default';
      rationale.push('No width info given — defaulted to ' + raw + ' (Standard). Measuring is recommended.');
    }
    // Never emit a width we don't make (A/C/E are interpretation-only grades).
    var code = snapToSupported(raw, cfg);
    if (code !== raw) rationale.push('Snapped ' + raw + ' to the nearest made width ' + code + '.');
    return { code: code, source: source };
  }

  function scoreConfidence(estimates, spreadMm, girth, width, cfg) {
    var hasMeasured = estimates.some(function (e) { return e.source === 'measured'; });
    var hasKnown = estimates.some(function (e) { return e.source === 'known'; });
    var chartAnchors = estimates.filter(function (e) { return e.source === 'chart'; }).length;
    var multiAgree = estimates.length >= 2 && spreadMm <= cfg.agreement_tolerance_size * 8;

    var conf;
    if ((hasMeasured && girth) || hasKnown || multiAgree) conf = 'high';
    else if (hasMeasured || chartAnchors >= 1) conf = 'medium';
    else conf = 'low';

    if (width.source === 'default' && conf === 'high') conf = 'medium';   // width is a guess
    return conf;
  }

  // ---- Public API ------------------------------------------------------------
  function recommend(answers, overrides) {
    var cfg = overrides ? deepMerge(CONFIG, overrides) : CONFIG;
    var gender = answers.gender === 'women' ? 'women' : 'men';
    var rationale = [], estimates = [];

    // measurements
    var girth = null, m = answers.measurement;
    if (m) {
      var lens = [m.leftLen, m.rightLen].map(Number).filter(function (x) { return x > 0; });
      if (lens.length) {
        var fl = Math.max.apply(null, lens);
        estimates.push({ mm: fl, source: 'measured', label: 'Measured foot' });
        rationale.push('We fit the larger foot: ' + fl + ' mm.');
      }
      var girths = [m.leftGirth, m.rightGirth].map(Number).filter(function (x) { return x > 0; });
      if (girths.length) girth = Math.max.apply(null, girths);
    }

    // known-size anchors
    (answers.anchors || []).forEach(function (a) {
      var r = anchorToLength(a, gender, cfg);
      if (r) { estimates.push({ mm: r.mm, source: r.source, label: (BRANDS[a.brand] || {}).label }); rationale.push(r.note); }
    });

    if (!estimates.length) {
      return { ok: false, error: 'need_input', message: 'Enter a size you already know, or your foot measurements.' };
    }

    // A measured foot is ground truth. When present it governs the length outright and the
    // brand-size guesses are dropped from the math (they stay in the rationale for context).
    // The larger-foot rule already collapsed left/right into a single measured estimate above.
    var measuredEst = estimates.filter(function (e) { return e.source === 'measured'; });
    var lengthEst = measuredEst.length ? measuredEst : estimates;
    if (measuredEst.length && measuredEst.length < estimates.length) {
      rationale.push('Used your measured foot for sizing; brand sizes are shown for reference only.');
    }

    // reconcile -> foot length
    var med = median(lengthEst.map(function (e) { return e.mm; }));
    var kept = lengthEst.filter(function (e) { return Math.abs(e.mm - med) <= cfg.outlier_drop_mm; });
    if (!kept.length) kept = lengthEst;
    var mms = kept.map(function (e) { return e.mm; });
    var flMm = Math.round(median(mms));
    var spread = Math.max.apply(null, mms) - Math.min.apply(null, mms);
    if (kept.length < lengthEst.length) rationale.push('Set aside ' + (lengthEst.length - kept.length) + ' input(s) that disagreed with the rest.');

    // No measured foot + brand sizes that still disagree by more than ~1.2 US sizes:
    // we will NOT guess a size. The UI routes these to measurement instead.
    var conflict = !measuredEst.length && lengthEst.length >= 2 && spread > cfg.reconcile_conflict_mm;
    if (conflict) rationale.push('The sizes entered disagree by ' + (spread / 8.47).toFixed(1) + ' sizes — too much to size confidently without a measurement.');

    // Fit problems are recorded for the made-to-fit build only — they never silently
    // change the recommended size/width. The standard length→size chart plus the measured
    // width govern, so the recommendation always matches the printed size chart.
    var problems = answers.problems || [];

    // size + width + confidence (deterministic: standard chart + measurements)
    var size = nearestRbSize(flMm, gender, cfg);
    var rng = cfg.size_range[gender] || cfg.size_range.men;
    size = Math.max(rng.min, Math.min(rng.max, size)); // clamp to offered range for this gender
    var width = resolveWidth(answers, girth, flMm, gender, cfg, rationale);
    var confidence = scoreConfidence(kept, spread, girth, width, cfg);
    var conv = euUk(size, gender);

    return {
      ok: true,
      gender: gender,
      recommendedSize: { us: size, eu: conv.eu, uk: conv.uk, euUkApprox: conv.approx },
      recommendedWidth: {
        code: width.code,
        name: WIDTH_NAME[width.code] || width.code,
        source: width.source
      },
      estimatedFootLengthMm: flMm,
      confidence: confidence,       // 'high' | 'medium' | 'low'
      spreadMm: spread,
      conflict: conflict,           // brand inputs can't be reconciled — do not guess; measure instead
      needsMeasurement: confidence === 'low' || width.source === 'default' || conflict,
      rationale: rationale,
      problems: problems,
      inputsUsed: estimates
    };
  }

  function deepMerge(base, over) {
    var out = {}, k;
    for (k in base) out[k] = (base[k] && typeof base[k] === 'object' && !Array.isArray(base[k])) ? deepMerge(base[k], {}) : base[k];
    for (k in over) out[k] = (over[k] && typeof over[k] === 'object' && !Array.isArray(over[k])) ? deepMerge(out[k] || {}, over[k]) : over[k];
    return out;
  }

  var api = { recommend: recommend, regionToUs: regionToUs, BRANDS: BRANDS, CONFIG: CONFIG, LADDER: LADDER, WIDTH_NAME: WIDTH_NAME };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.FitRecommender = api;
})(typeof window !== 'undefined' ? window : this);
